import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { fetchData, HTTP_STATUS, intl, LanguageBaseContext } from 'dmsbasereact';
import Swal from 'sweetalert2';
import moment from 'moment';
import { InvoiceDocumentStateEnum, InvoiceDocumentTypeEnum } from '../../constants/InvoiceDocument';

const createEmptyProduct = () => ({
    id: Date.now() + Math.random(),
    batchPk: '',
    quantity: '',
    partName: '',
    partDescription: '',
});

export const useCheckinCheckoutForm = ({ onDataChange, onCheckoutSubmitted, mode = 'checkin', organizationBranches = [], initialData = null }) => {
    const [danfeNumber, setDanfeNumber] = useState(initialData?.invoiceNumber || '');
    const [ownerEntityPk, setOwnerEntityPk] = useState(initialData?.ownerEntityPk || '');
    const [destinationEntityPk, setDestinationEntityPk] = useState('');
    const [products, setProducts] = useState(
        initialData?.batchPk
            ? [{ ...createEmptyProduct(), batchPk: initialData.batchPk, quantity: initialData.quantity?.toString() || '' }]
            : [createEmptyProduct()]
    );
    const [receivedAt, setReceivedAt] = useState(moment());
    const [sentAt, setSentAt] = useState(moment());
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [stockRefreshKey, setStockRefreshKey] = useState(0);

    const [checkinInvoices, setCheckinInvoices] = useState([]);
    const [isLoadingCheckinInvoices, setIsLoadingCheckinInvoices] = useState(false);
    const [selectedCheckinInvoice, setSelectedCheckinInvoice] = useState(null);

    const [checkoutDestinations, setCheckoutDestinations] = useState([]);
    const [checkoutOrigins, setCheckoutOrigins] = useState([]);
    const [isLoadingDestinations, setIsLoadingDestinations] = useState(false);
    const [isLoadingOrigins, setIsLoadingOrigins] = useState(false);

    const [batchesWithPart, setBatchesWithPart] = useState([]);
    const [isLoadingBatches, setIsLoadingBatches] = useState(false);

    const [checkoutBatchesWithStock, setCheckoutBatchesWithStock] = useState([]);
    const [isLoadingCheckoutBatches, setIsLoadingCheckoutBatches] = useState(false);

    const { language } = useContext(LanguageBaseContext);
    const pendingReceiverCnpjRef = useRef(null);

    const isCheckin = mode === 'checkin';
    const isDevolution = mode === 'devolution';

    useEffect(() => {
        if (initialData) {
            if (initialData.invoiceNumber !== undefined) {
                setDanfeNumber(initialData.invoiceNumber || '');
            }
            if (initialData.ownerEntityPk !== undefined) {
                setOwnerEntityPk(initialData.ownerEntityPk || '');
            }
            if (initialData.batchPk !== undefined) {
                setProducts([{
                    ...createEmptyProduct(),
                    batchPk: initialData.batchPk,
                    quantity: initialData.quantity?.toString() || ''
                }]);
            }
        }
    }, [initialData?.batchPk, initialData?.quantity, initialData?.invoiceNumber, initialData?.ownerEntityPk]);

    useEffect(() => {
        if (!isCheckin || isDevolution) {
            setBatchesWithPart([]);
            return;
        }

        const fetchBatches = async () => {
            setIsLoadingBatches(true);
            try {
                const response = await fetchData('/batch_location/batches-with-part', 'GET', true);
                if (response.status === HTTP_STATUS.OK_200) {
                    setBatchesWithPart(response.data || []);
                }
            } catch (error) {
                console.error('Failed to fetch batches with part info', error);
            } finally {
                setIsLoadingBatches(false);
            }
        };
        fetchBatches();
    }, [isCheckin]);

    // Fetch check-in invoices (for check-in mode)
    useEffect(() => {
        if (!isCheckin || isDevolution) return;

        const fetchCheckinInvoices = async () => {
            setIsLoadingCheckinInvoices(true);
            try {
                const response = await fetchData('/batch_location/checkin/invoices', 'GET', true);
                if (response.status === HTTP_STATUS.OK_200) {
                    setCheckinInvoices(response.data || []);
                }
            } catch (error) {
                console.error('Failed to fetch check-in invoices', error);
            } finally {
                setIsLoadingCheckinInvoices(false);
            }
        };
        fetchCheckinInvoices();
    }, [isCheckin]);

    // Fetch checkout origins (once on mode change)
    useEffect(() => {
        if (isCheckin && !isDevolution) return;

        const fetchOrigins = async () => {
            setIsLoadingOrigins(true);
            try {
                const originsResponse = await fetchData('/batch_location/checkout/origins', 'GET', true);
                if (originsResponse.status === HTTP_STATUS.OK_200) {
                    setCheckoutOrigins(originsResponse.data || []);
                }
            } catch (error) {
                console.error('Failed to fetch checkout origins', error);
            } finally {
                setIsLoadingOrigins(false);
            }
        };
        fetchOrigins();
    }, [isCheckin]);

    // Fetch checkout destinations (when origin changes)
    useEffect(() => {
        if (isCheckin && !isDevolution) return;

        if (!ownerEntityPk) {
            setCheckoutDestinations([]);
            setDestinationEntityPk('');
            return;
        }

        const fetchDestinations = async () => {
            setIsLoadingDestinations(true);
            setDestinationEntityPk('');
            try {
                const response = await fetchData(`/batch_location/checkout/destinations/${ownerEntityPk}`, 'GET', true);
                if (response.status === HTTP_STATUS.OK_200) {
                    setCheckoutDestinations(response.data || []);
                }
            } catch (error) {
                console.error('Failed to fetch checkout destinations', error);
                setCheckoutDestinations([]);
            } finally {
                setIsLoadingDestinations(false);
            }
        };
        fetchDestinations();
    }, [isCheckin, isDevolution, ownerEntityPk]);

    useEffect(() => {
        if ((isCheckin && !isDevolution) || !ownerEntityPk) {
            setCheckoutBatchesWithStock([]);
            return;
        }

        const fetchBatchesWithStock = async () => {
            setIsLoadingCheckoutBatches(true);
            try {
                const response = await fetchData(`/batch_location/institution/${ownerEntityPk}`, 'GET', true);
                if (response.status === HTTP_STATUS.OK_200) {
                    const batchesWithStock = (response.data || [])
                        .filter(item => item.quantity > 0)
                        .map(item => ({
                            pk: item.batch_pk,
                            number: item.batch_number,
                            part_name: item.part_name,
                            part_number: item.part_number,
                            part_description: null,
                            expiration: item.expiration,
                            available_quantity: item.quantity
                        }));
                    setCheckoutBatchesWithStock(batchesWithStock);
                }
            } catch (error) {
                console.error('Failed to fetch batches with stock', error);
                setCheckoutBatchesWithStock([]);
            } finally {
                setIsLoadingCheckoutBatches(false);
            }
        };
        fetchBatchesWithStock();
    }, [isCheckin, isDevolution, ownerEntityPk, stockRefreshKey]);

    // Auto-set destination when destinations finish loading and we have a pending CNPJ from XML import
    useEffect(() => {
        if (!pendingReceiverCnpjRef.current || !checkoutDestinations.length) return;
        const normalize = (cnpj) => (cnpj || '').replace(/\D/g, '');
        const pending = normalize(pendingReceiverCnpjRef.current);
        const match = checkoutDestinations.find(d => normalize(d.cnpj) === pending);
        if (match) {
            setDestinationEntityPk(match.pk);
        }
        pendingReceiverCnpjRef.current = null;
    }, [checkoutDestinations]);

    const handleXmlImport = useCallback(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetchData('/batch_location/checkout/parse-nfe', 'POST', true, formData, false, null, null, null, 'multipart/form-data', false);

            if (response.status !== HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: 'error',
                    title: intl.get('Error').d('Error'),
                    text: response?.data?.detail || intl.get('ErrorReadingXml').d('Error reading XML file.')
                });
                return;
            }

            const { invoice_number, emitter_cnpj, receiver_cnpj, emission_date, batches, batches_not_found, validation_errors } = response.data;

            if (validation_errors && validation_errors.length > 0) {
                Swal.fire({
                    icon: 'error',
                    title: intl.get('InvalidXmlFile').d('Invalid XML file.'),
                    html: validation_errors.map(e => `<p style="margin:4px 0">${e}</p>`).join(''),
                });
                return;
            }

            if (invoice_number) {
                setDanfeNumber(invoice_number);
            }

            if (emission_date) {
                setSentAt(moment(emission_date));
            }

            // Auto-fill origin from emitter CNPJ
            const normalize = (cnpj) => (cnpj || '').replace(/\D/g, '');
            if (emitter_cnpj) {
                const emitterNorm = normalize(emitter_cnpj);
                const originMatch = checkoutOrigins.find(o => normalize(o.cnpj) === emitterNorm);
                if (originMatch) {
                    setOwnerEntityPk(originMatch.pk);
                }
            }

            // Store receiver CNPJ — will be matched when checkoutDestinations finishes loading
            if (receiver_cnpj) {
                pendingReceiverCnpjRef.current = receiver_cnpj;
            }

            if (batches && batches.length > 0) {
                const importedProducts = batches.map(b => ({
                    id: Date.now() + Math.random(),
                    batchPk: b.batch_pk,
                    quantity: b.quantity.toString(),
                    partName: b.part_name || '',
                    partDescription: '',
                    batchNumber: b.batch_number,
                }));
                setProducts(importedProducts);

                const title = batches_not_found && batches_not_found.length > 0
                    ? intl.get('XmlImportedWithWarnings').d('XML imported with warnings')
                    : intl.get('XmlImportedSuccessfully').d('XML imported successfully');

                const count = intl.get('LotsImportedCount', { count: batches.length }).d(`${batches.length} lots imported`);
                const warningText = batches_not_found && batches_not_found.length > 0
                    ? `\n${intl.get('SomeLotsNotFound').d('Some lots were not found in the system')}: ${batches_not_found.join(', ')}`
                    : '';

                Swal.fire({
                    icon: batches_not_found && batches_not_found.length > 0 ? 'warning' : 'success',
                    title,
                    text: `${count}.${warningText}`,
                });
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: intl.get('Warning').d('Warning'),
                    text: intl.get('NoLotsFoundInXml').d('No lots found in the XML file.')
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: intl.get('Error').d('Error'),
                text: intl.get('ErrorReadingXml').d('Error reading XML file.')
            });
        }
    }, [language, checkoutOrigins, setDanfeNumber, setOwnerEntityPk, setProducts]);

    const handleCheckinInvoiceSelect = useCallback((invoicePk) => {
        const invoice = checkinInvoices.find(inv => inv.pk === invoicePk);
        setSelectedCheckinInvoice(invoice);

        if (invoice) {
            setDanfeNumber(invoice.danfe_number);
            setOwnerEntityPk(invoice.owner_entity_pk);
            setDestinationEntityPk(invoice.destination_entity_pk);

            if (invoice.items && invoice.items.length > 0) {
                const invoiceProducts = invoice.items.map(item => ({
                    id: Date.now() + Math.random(),
                    batchPk: item.batch_pk,
                    quantity: item.quantity?.toString() || '',
                    partName: item.part_name || '',
                    partDescription: item.part_description || '',
                    batchNumber: item.batch_number || '',
                }));
                setProducts(invoiceProducts);
            } else {
                setProducts([createEmptyProduct()]);
            }
        } else {
            setDanfeNumber('');
            setOwnerEntityPk('');
            setDestinationEntityPk('');
            setProducts([createEmptyProduct()]);
        }
    }, [checkinInvoices]);

    const addProduct = useCallback(() => {
        setProducts(prev => [...prev, createEmptyProduct()]);
    }, []);

    const removeProduct = useCallback((productId) => {
        setProducts(prev => prev.length > 1 ? prev.filter(p => p.id !== productId) : prev);
    }, []);

    const updateProduct = useCallback((productId, field, value) => {
        setProducts(prev => prev.map(p => {
            if (p.id !== productId) return p;

            const updated = { ...p, [field]: value };

            if (field === 'batchPk' && value) {
                const batchSource = (isCheckin && !isDevolution) ? batchesWithPart : checkoutBatchesWithStock;
                const batch = batchSource.find(b => b.pk === value);
                if (batch) {
                    updated.partName = batch.part_name || '';
                    updated.partDescription = batch.part_description || '';
                    updated.batchNumber = batch.number || '';
                }
            }

            return updated;
        }));
    }, [batchesWithPart, checkoutBatchesWithStock, isCheckin, isDevolution]);

    useEffect(() => {
        if (onDataChange) {
            onDataChange({
                products: products,
                invoiceNumber: danfeNumber,
                ownerEntityPk: ownerEntityPk,
                destinationEntityPk: destinationEntityPk
            });
        }
    }, [products, danfeNumber, ownerEntityPk, destinationEntityPk, onDataChange]);

    const resetForm = useCallback(() => {
        setDanfeNumber('');
        if (isCheckin && !isDevolution) {
            setOwnerEntityPk('');
            setDestinationEntityPk('');
            setSelectedCheckinInvoice(null);
        } else {
            setDestinationEntityPk('');
        }
        setProducts([createEmptyProduct()]);
        setReceivedAt(moment());
        setSentAt(moment());
        setNotes('');
    }, [isCheckin, isDevolution]);

    const handleSubmit = async () => {
        const validProducts = products.filter(p => p.batchPk && p.quantity);
        if (validProducts.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: intl.get('Warning').d('Warning'),
                text: intl.get('AtLeastOneProductRequired').d('At least one product with batch and quantity is required.')
            });
            return;
        }

        if (!danfeNumber) {
            Swal.fire({
                icon: 'warning',
                title: intl.get('Warning').d('Warning'),
                text: intl.get('InvoiceNumberRequired').d('Invoice number is required.')
            });
            return;
        }

        if (!ownerEntityPk) {
            Swal.fire({
                icon: 'warning',
                title: intl.get('Warning').d('Warning'),
                text: intl.get('OriginRequired').d('Origin is required.')
            });
            return;
        }

        if (!destinationEntityPk) {
            Swal.fire({
                icon: 'warning',
                title: intl.get('Warning').d('Warning'),
                text: intl.get('DestinationRequired').d('Destination is required.')
            });
            return;
        }

        // Check for insufficient stock on checkout
        let allowInsufficientStock = false;
        if (!isCheckin && !isDevolution) {
            const insufficientItems = validProducts.filter(p => {
                const stock = p.availableStock;
                return stock !== undefined && stock !== null && parseInt(p.quantity, 10) > stock;
            });

            if (insufficientItems.length > 0) {
                const batchList = insufficientItems.map(p => {
                    const batch = checkoutBatchesWithStock.find(b => b.pk === p.batchPk);
                    const batchLabel = batch?.number || `PK ${p.batchPk}`;
                    return `<li><strong>${batchLabel}</strong>: ${intl.get('Checkout.Requested').d('Requested')}: ${p.quantity}, ${intl.get('AvailableStock').d('Available')}: ${p.availableStock}</li>`;
                }).join('');

                const result = await Swal.fire({
                    icon: 'warning',
                    title: intl.get('Checkout.InsufficientStockTitle').d('Insufficient Stock'),
                    html: `
                        <p>${intl.get('Checkout.InsufficientStockMessage').d(
                            'One or more batches do not have sufficient stock. Proceeding will generate a notification to Eurofarma.'
                        )}</p>
                        <ul style="text-align: left;">${batchList}</ul>
                        <p><strong>${intl.get('Checkout.InsufficientStockConfirm').d(
                            'Do you want to proceed with the checkout?'
                        )}</strong></p>
                    `,
                    showCancelButton: true,
                    confirmButtonText: intl.get('Checkout.ProceedAnyway').d('Proceed'),
                    cancelButtonText: intl.get('Cancel').d('Cancel'),
                    confirmButtonColor: '#d33',
                });

                if (!result.isConfirmed) return;
                allowInsufficientStock = true;
            }
        }

        setIsSubmitting(true);
        try {
            if (isCheckin && !isDevolution && selectedCheckinInvoice) {
                const items = validProducts.map(product => ({
                    batch_pk: product.batchPk,
                    quantity: parseInt(product.quantity, 10)
                }));

                const confirmResponse = await fetchData('/batch_location/checkin/confirm', 'POST', true, {
                    invoice_pk: selectedCheckinInvoice.pk,
                    received_at: receivedAt ? receivedAt.toISOString() : null,
                    items: items
                }, false, true);

                if (confirmResponse.status !== HTTP_STATUS.OK_200) {
                    throw new Error(confirmResponse?.data?.detail || 'Failed to confirm check-in.');
                }
            } else if (!isCheckin && !isDevolution) {
                // Checkout: single endpoint that creates invoice + removes stock from source
                const checkoutPayload = {
                    source_organization_pk: ownerEntityPk,
                    destination_organization_pk: destinationEntityPk,
                    danfe_number: danfeNumber,
                    sent_at: sentAt ? sentAt.toISOString() : null,
                    notes: notes || null,
                    allow_insufficient_stock: allowInsufficientStock,
                    items: validProducts.map(p => ({
                        batch_pk: p.batchPk,
                        quantity: parseInt(p.quantity, 10),
                    })),
                };

                const checkoutResponse = await fetchData('/batch_location/check-out', 'POST', true, checkoutPayload, false, true);
                if (checkoutResponse.status !== HTTP_STATUS.OK_200) {
                    throw new Error(checkoutResponse?.data?.detail || intl.get('CheckOutFailed').d('Failed to check out.'));
                }
            } else {
                // Checkin (manual) or Devolution: create invoice + items via generic routes
                const invoiceState = isDevolution
                    ? InvoiceDocumentStateEnum.DEVOLUTION
                    : InvoiceDocumentStateEnum.DONE;

                const invoicePayload = {
                    danfe_number: danfeNumber,
                    owner_entity_pk: ownerEntityPk,
                    destination_entity_pk: destinationEntityPk,
                    invoice_type: isDevolution ? InvoiceDocumentTypeEnum.OUTBOUND : InvoiceDocumentTypeEnum.INBOUND,
                    invoice_state: invoiceState,
                };

                const invoiceResponse = await fetchData('/invoice_document', 'POST', true, invoicePayload, false, true);
                if (invoiceResponse.status !== HTTP_STATUS.CREATED_201) {
                    throw new Error(invoiceResponse?.data?.detail || 'Failed to create invoice document.');
                }

                const invoice_pk = invoiceResponse.data.data.pk;
                const destinationBranch = organizationBranches.find(e => e.pk === destinationEntityPk);

                for (const product of validProducts) {
                    const itemPayload = {
                        invoice_pk: invoice_pk,
                        quantity: parseInt(product.quantity, 10),
                        batch_pk: product.batchPk,
                        received_at: receivedAt ? receivedAt.toISOString() : null,
                        sent_at: sentAt ? sentAt.toISOString() : null,
                        destination_cnpj: destinationBranch?.cnpj || null,
                        notes: notes || null,
                    };

                    if (isNaN(itemPayload.quantity)) {
                        throw new Error(intl.get('InvalidQuantity').d('Quantity must be a valid number.'));
                    }

                    const itemResponse = await fetchData('/invoice_document_item', 'POST', true, itemPayload, false, true);
                    if (itemResponse.status !== HTTP_STATUS.CREATED_201) {
                        throw new Error(itemResponse?.data?.detail || 'Failed to create invoice item.');
                    }
                }
            }

            let successMessage;
            if (isDevolution) {
                successMessage = intl.get('DevolutionRegisteredSuccessfully').d('Devolution registered successfully.');
            } else if (isCheckin) {
                successMessage = intl.get('CheckInRegisteredSuccessfully').d('Check-in registered successfully.');
            } else {
                successMessage = intl.get('CheckOutRegisteredSuccessfully').d('Check-out registered successfully.');
            }

            Swal.fire({
                icon: 'success',
                title: intl.get('Success').d('Success'),
                text: successMessage
            });
            resetForm();
            setStockRefreshKey(prev => prev + 1);

            if (onCheckoutSubmitted && !isCheckin) {
                onCheckoutSubmitted();
            }

            if (isCheckin && !isDevolution) {
                const response = await fetchData('/batch_location/checkin/invoices', 'GET', true);
                if (response.status === HTTP_STATUS.OK_200) {
                    setCheckinInvoices(response.data || []);
                }
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: intl.get('Error').d('Error'),
                text: error.message || intl.get('AnErrorOccurred').d('An error occurred.')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        state: {
            danfeNumber,
            ownerEntityPk,
            destinationEntityPk,
            products,
            receivedAt,
            sentAt,
            notes,
            isSubmitting,
            isCheckin,
            isDevolution,
            stockRefreshKey,
            checkinInvoices,
            isLoadingCheckinInvoices,
            selectedCheckinInvoice,
            checkoutDestinations,
            checkoutOrigins,
            isLoadingDestinations,
            isLoadingOrigins,
            checkoutBatchesWithStock,
            isLoadingCheckoutBatches,
            batchesWithPart,
            isLoadingBatches,
        },
        handlers: {
            setDanfeNumber,
            setOwnerEntityPk,
            setDestinationEntityPk,
            addProduct,
            removeProduct,
            updateProduct,
            setReceivedAt,
            setSentAt,
            setNotes,
            handleSubmit,
            handleCheckinInvoiceSelect,
            handleXmlImport,
        }
    };
};
